import React, { useMemo, useState } from 'react';
import { useObservable } from 'react-use-observable';

import { Card, DatePicker, Form, Skeleton, Typography } from 'antd';

import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import Spin from 'components/Spin';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { formatDate } from 'helpers/date';
import DashboardSegmented from 'v2/components/Pages/Dashboard/DashboardSegmented';
import { getLastSevenDaysPeriod } from 'v2/components/Pages/Dashboard/period';
import HelpTip from 'v2/components/Shared/HelpTip';
import { Mask } from 'v2/components/Shared/ValuesVisibility';
import dashboardService from 'v2/services/dashboard';

import Chart from './Chart';

const { RangePicker } = DatePicker;

type TabOption = {
  title: string;
  key: TabKeys;
  isMonetary?: boolean;
  tip?: string;
};

enum TabKeys {
  SALES = 'sales',
  REVENUES = 'revenues',
  GAINS = 'gains'
}

type Period = {
  start: Date;
  end: Date;
};

const tabOptions: ReadonlyArray<TabOption> = [
  { title: 'Vendas', key: TabKeys.SALES, isMonetary: false },
  {
    title: 'Faturamento',
    key: TabKeys.REVENUES,
    isMonetary: true
  },
  { title: 'Ganhos', key: TabKeys.GAINS, isMonetary: true }
] as const;

const disabledDate = (current: Dayjs) => current.isAfter(dayjs(), 'day');

const LineChart = () => {
  const [period, setPeriod] = useState<Period>(getLastSevenDaysPeriod);
  const [selectedTab, setSelectedTab] = useState<TabKeys>(TabKeys.REVENUES);

  const [isLoading] = useObservable(() => dashboardService.getLoadingPeriodSales(), []);
  const [periodSalesGains] = useObservable(
    () => dashboardService.getPeriodSalesGains(period.start, period.end),
    [period]
  );

  const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates) {
      return;
    }

    const [start, end] = dates;
    setPeriod({
      start: start ? start.toDate() : period.start,
      end: end ? end.toDate() : period.end
    });
  };

  const totalsByKey = useMemo(() => {
    const sumFor = (key: TabKeys) => {
      const arr = periodSalesGains?.[key] ?? [];
      return arr.reduce((acc, item) => acc + (item.producer ?? 0) + (item.coproducer ?? 0) + (item.affiliate ?? 0), 0);
    };

    return {
      sales: sumFor(TabKeys.SALES),
      revenues: sumFor(TabKeys.REVENUES),
      gains: sumFor(TabKeys.GAINS)
    };
  }, [periodSalesGains]);

  const chartData = useMemo(
    () =>
      (periodSalesGains?.[selectedTab] ?? []).map(item => ({
        ...item,
        date: formatDate(item.date, 'dd/MM')
      })),
    [selectedTab, periodSalesGains]
  );

  const tabItems = tabOptions.map(tabOption => {
    const total = totalsByKey[tabOption.key];
    return {
      value: tabOption.key,
      disabled: isLoading,
      label: (
        <div className='flex flex-col items-center justify-center py-1'>
          <div className='flex items-center gap-1'>
            <Typography.Text className='text-sm'>{tabOption.title}</Typography.Text>
            {tabOption.tip && <HelpTip text={tabOption.tip} />}
          </div>
          {isLoading ? (
            <Skeleton.Input active size='small' className='mt-1' />
          ) : tabOption.isMonetary ? (
            <Typography.Text>
              <Mask>
                <ColoredFormattedNumber format='BRL' value={total} />
              </Mask>
            </Typography.Text>
          ) : (
            <Typography.Text>
              <Mask>{total}</Mask>
            </Typography.Text>
          )}
        </div>
      )
    };
  });

  const rangeValue: [Dayjs, Dayjs] = [dayjs(period.start), dayjs(period.end)];

  return (
    <Card
      className='h-full flex-1'
      cover={
        <div className='p-1'>
          <DashboardSegmented
            value={selectedTab}
            onChange={value => setSelectedTab(value as TabKeys)}
            options={tabItems}
          />
        </div>
      }
    >
      <Form.Item>
        <Typography.Text>Data de pagamento:</Typography.Text>
        <RangePicker
          className='mt-1 w-full'
          value={rangeValue}
          onChange={handleDateChange}
          format='DD/MM/YYYY'
          size='middle'
          disabledDate={disabledDate}
          placeholder={['Data inicial', 'Data final']}
          disabled={isLoading}
        />
      </Form.Item>
      {isLoading ? (
        <div className='flex min-h-70 items-center justify-center'>
          <Spin />
        </div>
      ) : (
        <Chart data={chartData} totalGains={totalsByKey.gains} />
      )}
    </Card>
  );
};

export default LineChart;
